import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { myExpariencesService, userService } from "../../../services";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useDebounce, useViewport } from "../../../helpers";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";
import "./game-leaderboard.css";
import { formatTimeScore } from "../../../helpers/format-leaderboard-time";
import { useLocation, useSearchParams } from "react-router-dom";
import CryptoJs from "crypto-js";
import { Buffer } from "buffer";
import CopyToClipboard from "react-copy-to-clipboard";
import { FileDownload, Launch, Link, PlayArrow } from "@mui/icons-material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useSelector } from "react-redux";
import { useCountries } from "use-react-countries";
import { getCountryCodeFromFlagPath } from "../../../components/translationsEditor/translationsHelper";

const PREFIX = "GameLeaderboard";

const classes = {
  leaderBoard: `${PREFIX}-leaderBoard`,
  leaderBoardShareButton: `${PREFIX}-leaderBoardShareButton`,
  leaderboardAddButton: `${PREFIX}-leaderboardAddButton`,
  leaderboardDownloadButton: `${PREFIX}-leaderboardDownloadButton`,
  standaloneLeaderBoard: `${PREFIX}-standaloneLeaderBoard`,
  leaderBoardDialogContainer: `${PREFIX}-leaderBoardDialogContainer`,
  leaderBoardHeader: `${PREFIX}-leaderBoardHeader`,
  leaderBoardHeaderLogo: `${PREFIX}-leaderBoardHeaderLogo`,
  leaderBoardHeaderLogoMobile: `${PREFIX}-leaderBoardHeaderLogoMobile`,
  leaderBoardHeaderLogoMobileContainer: `${PREFIX}-leaderBoardHeaderLogoMobileContainer`,
  leaderBoardExparienceName: `${PREFIX}-leaderBoardExparienceName`,
  leaderBoardExparienceNameContainer: `${PREFIX}-leaderBoardExparienceNameContainer`,
  leaderBoardTitleContainer: `${PREFIX}-leaderBoardTitleContainer`,
  leaderBoardTitleImage: `${PREFIX}-leaderBoardTitleImage`,
  leaderBoardTitle: `${PREFIX}-leaderBoardTitle`,
  leaderBoardScoreContainer: `${PREFIX}-leaderBoardScoreContainer`,
  leaderBoardScore: `${PREFIX}-leaderBoardScore`,
  leaderBoardScoreEntry: `${PREFIX}-leaderBoardScoreEntry`,
  leaderBoardScoreRank: `${PREFIX}-leaderBoardScoreRank`,
  leaderBoardScoreFlagContainer: `${PREFIX}-leaderBoardScoreFlagContainer`,
  leaderBoardScoreFlagImage: `${PREFIX}-leaderBoardScoreFlagImage`,
  leaderBoardScoreFlag: `${PREFIX}-leaderBoardScoreFlag`,
  leaderBoardScorePseudo: `${PREFIX}-leaderBoardScorePseudo`,
  leaderBoardScoreScore: `${PREFIX}-leaderBoardScoreScore`,
  leaderBoardHeaderLogoPosition: `${PREFIX}-leaderBoardHeaderLogoPosition`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.leaderBoard}`]: {
    maxWidth: "100%",
    // height: "690px",
    paddingBottom: "1.5rem",
    margin: "0 auto 0.75rem auto",
    backgroundColor: "#181C22",
    border: "8px solid white",
    borderRadius: "40px",
  },
  [`& .${classes.leaderBoardDialogContainer}`]: {
    display: "flex",
    flexDirection: "column",
    maxWidth: "400px",
  },
  [`& .${classes.standaloneLeaderBoard}`]: {
    width: "90%",
    padding: "0.75rem",
    margin: "0 auto ",
    backgroundColor: "#181C22",
    border: "8px solid white",
    borderRadius: "40px",
  },
  [`& .${classes.leaderBoardShareButton}`]: {
    position: "absolute",
    top: "12px",
    right: "20px",
    zIndex: 9999,
    backgroundColor: "#424242",
    color: "white",
    "&:hover": {
      backgroundColor: "black",
      color: "#D3AF5F",
    },
  },
  [`& .${classes.leaderboardAddButton}`]: {
    position: "absolute",
    top: "12px",
    right: "70px",
    width: "40px",
    height: "40px",
    zIndex: 9999,
    backgroundColor: "#424242",
    color: "white",
    "&:hover": {
      backgroundColor: "black",
      color: "#D3AF5F",
    },
  },
  [`& .${classes.leaderboardDownloadButton}`]: {
    position: "absolute",
    top: "12px",
    right: "120px",
    width: "40px",
    height: "40px",
    zIndex: 9999,
    backgroundColor: "#424242",
    color: "white",
    "&:hover": {
      backgroundColor: "black",
      color: "#D3AF5F",
    },
  },
  [`& .${classes.leaderBoardHeader}`]: {
    display: "flex",
    justifyContent: "space-evenly",
    alignItems: "center",
    [theme.breakpoints.down("md")]: {
      justifyContent: "center",
    },
  },
  [`& .${classes.leaderBoardHeaderLogo}`]: {
    width: "50px",
    height: "50px",
    margin: "0 1rem",
    [theme.breakpoints.down("md")]: {
      width: "35px",
      height: "35px",
    },
  },
  [`& .${classes.leaderBoardHeaderLogoPosition}`]: {
    width: "50px",
    height: "50px",
    margin: "0 1rem",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  [`& .${classes.leaderBoardHeaderLogoMobileContainer}`]: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: "15px",
  },
  [`& .${classes.leaderBoardHeaderLogoMobile}`]: {
    width: "45px",
    height: "45px",
    margin: "0 auto",
  },
  [`& .${classes.leaderBoardTitleContainer}`]: {
    height: "60px",
    padding: "0 10px",
    backgroundColor: "#0A0C10",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    [theme.breakpoints.down("md")]: {
      width: "60%",
      height: "50px",
      padding: "0 8px",
    },
  },
  [`& .${classes.leaderBoardTitleImage}`]: {
    width: "50px",
    [theme.breakpoints.down("md")]: {
      width: "20px",
    },
  },
  [`& .${classes.leaderBoardTitle}`]: {
    margin: 0,
    fontFamily: "Leaderboard",
    fontSize: "30px",
    color: "#FFCF00",
    [theme.breakpoints.down("md")]: {
      fontSize: "18px",
    },
  },
  [`& .${classes.leaderBoardExparienceNameContainer}`]: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "0 20px",
    [theme.breakpoints.down("md")]: {},
  },
  [`& .${classes.leaderBoardExparienceName}`]: {
    margin: "5px 2px 0 0",
    fontFamily: "Leaderboard",
    color: "#fff",
    fontSize: "40px",
    display: "-webkit-box",
    WebkitLineClamp: "2",
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
    textAlign: "center",

    [theme.breakpoints.down("md")]: {
      fontSize: "25px",
    },
  },
  [`& .${classes.leaderBoardScore}`]: {
    width: "100%",
    height: "45px",
    backgroundColor: "#0A0C10",
    marginBottom: "8px",
    [theme.breakpoints.down("md")]: {
      height: "40px",
    },
  },
  [`& .${classes.leaderBoardScoreEntry}`]: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingLeft: "1.5rem",
  },
  [`& .${classes.leaderBoardScoreRank}`]: {
    fontFamily: "Leaderboard",
    textAlign: "center",
    fontSize: "35px",
    color: "#FFCF00",
    [theme.breakpoints.down("md")]: {
      fontSize: "18px",
    },
  },
  [`& .${classes.leaderBoardScoreFlagContainer}`]: {
    width: "15%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    [theme.breakpoints.down("md")]: {
      width: "10%",
    },
  },
  [`& .${classes.leaderBoardScoreFlagImage}`]: {
    height: "30px",
    borderRadius: "2px",
    maxWidth: "40px",
    [theme.breakpoints.down("md")]: {
      height: "25px",
    },
  },
  [`& .${classes.leaderBoardScorePseudo}`]: {
    width: "30%",
    fontFamily: "Leaderboard",
    fontSize: "35px",
    color: "white",
    overflow: "hidden",
    textOverflow: "ellipsis",
    [theme.breakpoints.down("md")]: {
      fontSize: "18px",
    },
  },
  [`& .${classes.leaderBoardScoreScore}`]: {
    width: "30%",
    fontFamily: "Leaderboard",
    textAlign: "center",
    fontSize: "35px",
    color: "red",
    overflow: "hidden",
    [theme.breakpoints.down("md")]: {
      fontSize: "18px",
    },
  },
}));

const GameLeaderboards = ({
  exparienceId,
  gamesList = null,
  exparienceName = null,
  exparienceDisplayName = null,
  standalone = false,
}) => {
  const { t } = useTranslation();
  const { width } = useViewport();
  const { countries } = useCountries();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const [fetching, setFetching] = useState(false);
  const [data, setData] = useState(null);
  const [savingData, setSavingData] = useState(false);
  const [xpInfos, setXpInfos] = useState(null);
  const [decryptedValues, setDecryptedValues] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchedUsers, setFetchedUsers] = useState([]);
  const [searchUser, setSearchUser] = useState("");
  const debouncedSearchUser = useDebounce(searchUser, 800);

  const [state, setState] = useState({
    gameKind: "practice",
    gameLevel: "easy",
    gameCategory: !standalone
      ? gamesList[0]?.type?.replace(/[0-9]/g, "") || "basket"
      : "basket",
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [addScoreModal, setAddScoreModal] = useState(false);
  const [newScore, setNewScore] = useState({
    pseudo: "",
    user: null,
    flag: null,
    score: "",
  });
  const initialized = useRef(false);
  const prevState = usePrevious(state);

  const isAdmin = useSelector(
    (state) => state.user?.currentUser?.account?.role === "ADMIN"
  );

  function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const fetchStats = useCallback(
    (decryptedParams) => {
      setFetching(true);
      const myCall = standalone
        ? myExpariencesService.getStandaloneLeaderboard(
            decryptedParams.id,
            decryptedParams.kind,
            state.gameLevel,
            decryptedParams.category
          )
        : myExpariencesService.getLeaderboardsForStats(
            exparienceId,
            state.gameKind,
            state.gameLevel,
            state.gameCategory
          );
      initialized.current = true;
      myCall
        .then((res) => {
          let myRes = [...res.bestScores];
          myRes.length = 10;
          if (res.bestScores.length < myRes.length)
            myRes.fill({}, res.bestScores.length, myRes.length);
          setData(myRes);
          if (res.dm) setXpInfos(res.dm);
          const inter = setTimeout(() => {
            setFetching(false);
            clearTimeout(inter);
          }, 500);
        })
        .catch((err) => {
          console.log("error in fetching stats =>", err);
          enqueueSnackbar(t("toasts.filterStatsFailed"), { variant: "error" });
          setData([]);
          setFetching(false);
          const inter = setTimeout(() => {
            setFetching(false);
            clearTimeout(inter);
          }, 500);
        });
    },
    [exparienceId, state, enqueueSnackbar, t, standalone]
  );

  const decryptValue = (param) => {
    try {
      const bytes = CryptoJs.AES.decrypt(
        atob(param),
        process.env.REACT_APP_ENCRYPT_DECRYPT_KEY
      );
      return bytes.toString(CryptoJs.enc.Utf8);
    } catch {
      return undefined;
    }
  };

  const handleClickOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleClickCloseDialog = () => {
    setOpenDialog(false);
  };

  const refreshData = () => {
    fetchStats(decryptedValues);
  };

  const deleteScore = (scoreId) => {
    myExpariencesService
      .deleteLeaderboardScore(scoreId, exparienceId)
      .then((res) => {
        setSavingData(false);
        let myRes = [...res.bestScores];
        myRes.length = 10;
        if (res.bestScores.length < myRes.length)
          myRes.fill({}, res.bestScores.length, myRes.length);
        setData(myRes);
        if (res.dm) setXpInfos(res.dm);
      })
      .catch((err) => {
        setSavingData(false);
        console.log("err =>", err);
        enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
      });
  };

  const exportData = () => {
    console.log("export");
  };

  const addScoreToLeaderboard = () => {
    const tmpScore = { score: newScore.score };
    if (state.gameKind === "route") tmpScore.time = newScore.score;
    const dataObject = {
      pseudo: newScore.pseudo,
      flag: newScore.flag.flag,
      score: tmpScore,
      userId: newScore.user.id,
      category: state.gameCategory,
      kind: state.gameKind,
      exparienceId,
      level: state.gameLevel,
    };

    setSavingData(true);
    myExpariencesService
      .addLeaderboardScore(dataObject)
      .then((res) => {
        let myRes = [...res.exparienceGame.bestScores];
        myRes.length = 10;
        if (res.exparienceGame.bestScores.length < myRes.length)
          myRes.fill({}, res.exparienceGame.bestScores.length, myRes.length);
        setData(myRes);
        if (res.dm) setXpInfos(res.dm);
        setSavingData(false);
        setAddScoreModal(false);
        setNewScore({
          pseudo: "",
          user: null,
          flag: null,
          score: "",
        });
      })
      .catch((err) => {
        setSavingData(false);
        console.log("err =>", err);
        enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
      });
  };

  useEffect(() => {
    if (standalone && data === null && !initialized.current) {
      const decryptedParams = {
        id: decryptValue(searchParams.get("xp")),
        kind: decryptValue(searchParams.get("gk")),
        category: decryptValue(searchParams.get("gc")),
      };
      setDecryptedValues(decryptedParams);
      fetchStats(decryptedParams);
    } else if (
      gamesList?.length > 0 &&
      JSON.stringify(prevState) !== JSON.stringify(state)
    ) {
      fetchStats();
    }
  }, [state, fetchStats, prevState, gamesList, data, standalone, searchParams]);

  const iconsAndTitleToDisplay = useMemo(
    () => ({
      basket: {
        practice: {
          render: (
            <Box
              color="primary"
              size="medium"
              sx={{
                marginBottom: "5px",
              }}
            >
              <img
                src="/gamesIcons/basket/practice.png"
                alt="basket practice"
                style={{ width: "30px", height: "30px" }}
              />
            </Box>
          ),
          title: (
            <Typography sx={{ color: "white" }}>
              {t("myExpariences.stats.leaderboards.practice")}
            </Typography>
          ),
        },
        route: {
          render: (
            <Box
              color="primary"
              size="medium"
              sx={{
                marginBottom: "5px",
              }}
            >
              <img
                src="/gamesIcons/basket/route.png"
                alt="basket route"
                style={{ width: "30px", height: "30px" }}
              />
            </Box>
          ),
          title: (
            <Typography sx={{ color: "white" }}>
              {t("myExpariences.stats.leaderboards.basketRoute")}
            </Typography>
          ),
        },
        contest: {
          render: (
            <Box
              color="primary"
              size="medium"
              sx={{
                marginBottom: "5px",
              }}
            >
              <img
                src="/gamesIcons/basket/contest.png"
                alt="basket contest"
                style={{ width: "30px", height: "30px" }}
              />
            </Box>
          ),
          title: (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "start",
              }}
            >
              <Typography sx={{ color: "white" }}>
                {t("myExpariences.stats.leaderboards.contest")}
              </Typography>
              <Typography sx={{ color: "white" }}>
                {t("myExpariences.stats.leaderboards.contest2")}
              </Typography>
            </Box>
          ),
        },
      },
      football: {
        practice: {
          render: (
            <Box
              color="primary"
              size="medium"
              sx={{
                marginBottom: "5px",
              }}
            >
              <img
                src="/gamesIcons/football/practice.png"
                alt="football practice"
                style={{ width: "30px", height: "30px" }}
              />
            </Box>
          ),
          title: (
            <Typography sx={{ color: "white" }}>
              {t("myExpariences.stats.leaderboards.practice")}
            </Typography>
          ),
        },
        route: {
          render: (
            <Box
              color="primary"
              size="medium"
              sx={{
                marginBottom: "5px",
              }}
            >
              <img
                src="/gamesIcons/football/route.png"
                alt="football route"
                style={{ width: "30px", height: "30px" }}
              />
            </Box>
          ),
          title: (
            <Typography sx={{ color: "white" }}>
              {t("myExpariences.stats.leaderboards.route")}
            </Typography>
          ),
        },
        contest: {
          render: (
            <Box
              color="primary"
              size="medium"
              sx={{
                marginBottom: "5px",
              }}
            >
              <img
                src="/gamesIcons/football/contest.png"
                alt="football contest"
                style={{ width: "30px", height: "30px" }}
              />
            </Box>
          ),
          title: (
            <Typography sx={{ color: "white" }}>
              {t("myExpariences.stats.leaderboards.contestFootball")}
            </Typography>
          ),
        },
      },
      arcade: {
        render: (
          <Box
            color="primary"
            size="medium"
            sx={{
              marginBottom: "5px",
            }}
          >
            <img
              src="/gamesIcons/arcade/practice.png"
              alt="arcade practice"
              style={{ width: "30px", height: "30px" }}
            />
          </Box>
        ),
        title: (
          <Typography sx={{ color: "white" }}>
            {t("myExpariences.stats.leaderboards.arcadePractice")}
          </Typography>
        ),
      },
    }),
    [t]
  );

  const iconButtonList = useMemo(() => {
    return {
      basket: (
        <Box
          sx={{
            marginTop: "1rem",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <IconButton
              color="primary"
              size="medium"
              sx={{
                backgroundColor:
                  state.gameKind === "practice"
                    ? "#D3Af5f !important"
                    : "inherit",
                marginBottom: "5px",
              }}
              onClick={() =>
                setState({
                  ...state,
                  gameKind: "practice",
                  gameCategory: "basket",
                })
              }
            >
              <img
                src="/gamesIcons/basket/practice.png"
                alt="basket practice"
                style={{ width: "30px", height: "30px" }}
              />
            </IconButton>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "60px",
              }}
            >
              <Typography sx={{ color: "white" }}>
                {t("myExpariences.stats.leaderboards.practice")}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <IconButton
              color="primary"
              size="medium"
              sx={{
                backgroundColor:
                  state.gameKind === "route" ? "#D3Af5f !important" : "inherit",
                marginBottom: "5px",
              }}
              onClick={() =>
                setState({
                  ...state,
                  gameKind: "route",
                  gameCategory: "basket",
                })
              }
            >
              <img
                src="/gamesIcons/basket/route.png"
                alt="basket route"
                style={{ width: "30px", height: "30px" }}
              />
            </IconButton>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "60px",
              }}
            >
              <Typography sx={{ color: "white" }}>
                {t("myExpariences.stats.leaderboards.basketRoute")}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <IconButton
              color="primary"
              size="medium"
              sx={{
                backgroundColor:
                  state.gameKind === "contest"
                    ? "#D3Af5f !important"
                    : "inherit",
                marginBottom: "5px",
              }}
              onClick={() =>
                setState({
                  ...state,
                  gameKind: "contest",
                  gameCategory: "basket",
                })
              }
            >
              <img
                src="/gamesIcons/basket/contest.png"
                alt="basket contest"
                style={{
                  width: "30px",
                  height: "30px",
                }}
              />
            </IconButton>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "start",
                height: "60px",
              }}
            >
              <Typography sx={{ color: "white" }}>
                {t("myExpariences.stats.leaderboards.contest")}
              </Typography>
              <Typography sx={{ color: "white" }}>
                {t("myExpariences.stats.leaderboards.contest2")}
              </Typography>
            </Box>
          </Box>
        </Box>
      ),
      football: (
        <Box
          sx={{
            marginTop: "1rem",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <IconButton
              color="primary"
              size="medium"
              sx={{
                backgroundColor:
                  state.gameKind === "practice"
                    ? "#D3Af5f !important"
                    : "inherit",
                marginBottom: "5px",
              }}
              onClick={() =>
                setState({
                  ...state,
                  gameKind: "practice",
                  gameCategory: "football",
                })
              }
            >
              <img
                src="/gamesIcons/football/practice.png"
                alt="football practice"
                style={{ width: "30px", height: "30px" }}
              />
            </IconButton>
            <Typography sx={{ color: "white" }}>
              {t("myExpariences.stats.leaderboards.practice")}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <IconButton
              color="primary"
              size="medium"
              sx={{
                backgroundColor:
                  state.gameKind === "route" ? "#D3Af5f !important" : "inherit",
                marginBottom: "5px",
              }}
              onClick={() =>
                setState({
                  ...state,
                  gameKind: "route",
                  gameCategory: "football",
                })
              }
            >
              <img
                src="/gamesIcons/football/route.png"
                alt="football route"
                style={{ width: "30px", height: "30px" }}
              />
            </IconButton>
            <Typography sx={{ color: "white" }}>
              {t("myExpariences.stats.leaderboards.route")}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <IconButton
              color="primary"
              size="medium"
              sx={{
                backgroundColor:
                  state.gameKind === "contest"
                    ? "#D3Af5f !important"
                    : "inherit",
                marginBottom: "5px",
              }}
              onClick={() =>
                setState({
                  ...state,
                  gameKind: "contest",
                  gameCategory: "football",
                })
              }
            >
              <img
                src="/gamesIcons/football/contest.png"
                alt="football contest"
                style={{
                  width: "30px",
                  height: "30px",
                }}
              />
            </IconButton>
            <Typography sx={{ color: "white" }}>
              {t("myExpariences.stats.leaderboards.contestFootball")}
            </Typography>
          </Box>
        </Box>
      ),
      arcade: (
        <Box
          sx={{
            marginTop: "1rem",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <IconButton
              color="primary"
              size="medium"
              sx={{
                backgroundColor:
                  state.gameKind === "practice"
                    ? "#D3Af5f !important"
                    : "inherit",
                marginBottom: "5px",
              }}
              onClick={() =>
                setState({
                  ...state,
                  gameKind: "practice",
                  gameCategory: "arcade",
                })
              }
            >
              <img
                src="/gamesIcons/arcade/practice.png"
                alt="arcade practice"
                style={{ width: "30px", height: "30px" }}
              />
            </IconButton>
            <Typography sx={{ color: "white" }}>
              {t("myExpariences.stats.leaderboards.arcadePractice")}
            </Typography>
          </Box>
        </Box>
      ),
    };
  }, [state, t]);

  const encryptParam = (param) => {
    return Buffer.from(
      CryptoJs.AES.encrypt(
        param,
        process.env.REACT_APP_ENCRYPT_DECRYPT_KEY
      ).toString()
    ).toString("base64");
  };

  const encryptUrl = () => {
    return `${window.location.origin}/leaderboard?xp=${encryptParam(
      exparienceId
    )}&gk=${encryptParam(state.gameKind)}&gc=${encryptParam(
      state.gameCategory
    )}`;
  };

  const checkForm = () => {
    if (
      newScore.user &&
      newScore.flag?.emoji?.length > 0 &&
      newScore.pseudo?.length > 0
    ) {
      if (
        newScore.score.length >= 0 &&
        /[0-9]/.test(newScore.score)
        //   ||
        // (state.gameKind === "route" &&
        //   /[0-9]{2}[:][0-9]{2}[:][0-9]{3}/.test(newScore.score))
      ) {
        return false;
      } else {
        return true;
      }
    } else {
      return true;
    }
  };

  useEffect(() => {
    if (debouncedSearchUser.length > 0) {
      setLoading(true);
      userService.searchUser(debouncedSearchUser).then((res) => {
        setFetchedUsers(res);
        setLoading(false);
      });
    }
  }, [debouncedSearchUser]);

  if (!data) {
    return (
      <div className="loading-container">
        <CircularProgress size={50} />
        <Typography>{t("loaders.fetching")}</Typography>
      </div>
    );
  } else if (
    (!gamesList && !standalone) ||
    (!standalone && gamesList?.length === 0) ||
    (standalone && !data) ||
    (standalone && !decryptedValues)
  )
    return (
      <div className="loading-container">
        <Typography variant="body1">
          {t("myExpariences.stats.leaderboards.noGames")}
        </Typography>
      </div>
    );
  else {
    const gameIndex = standalone
      ? decryptedValues.category
      : gamesList.findIndex((game) => {
          return state.gameCategory === "basket"
            ? game.type === "basket2"
            : game.type === state.gameCategory;
        });
    const encryptedUrl = encryptUrl();

    return (
      <Root>
        <Dialog
          open={openDialog}
          onClose={handleClickCloseDialog}
          aria-labelledby={t("myExpariences.stats.leaderboards.embed")}
          PaperProps={{
            style: {
              minWidth: width < 769 ? "350px" : "650px",
            },
          }}
        >
          <DialogTitle id={t("myExpariences.stats.leaderboards.embed")}>
            {t("myExpariences.stats.leaderboards.shareLeaderboard")}
          </DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <TextField
              sx={{ marginLeft: "20px", width: width < 769 ? "210px" : "100%" }}
              variant="outlined"
              value={encryptedUrl}
              fullWidth
              disabled
            />
            <CopyToClipboard text={encryptedUrl}>
              <IconButton
                sx={{ marginLeft: "10px" }}
                aria-label="copy link"
                color="primary"
                onClick={(e) => {
                  enqueueSnackbar(t("toasts.copiedInClipboard"), {
                    variant: "info",
                  });
                  e.stopPropagation();
                }}
                size="large"
              >
                <Link />
              </IconButton>
            </CopyToClipboard>
            <a href={encryptedUrl} target="_blank" rel="noopener noreferrer">
              <IconButton aria-label="open link" color="primary" size="large">
                <Launch />
              </IconButton>
            </a>
          </Box>

          <DialogTitle id={t("myExpariences.stats.leaderboards.embed")}>
            {t("myExpariences.stats.leaderboards.embedLeaderboard")}
          </DialogTitle>
          <Box sx={{ marginBottom: "20px" }}>
            <TextField
              sx={{
                marginLeft: "20px",
                marginRight: "20px",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                width: width < 769 ? "310px" : "610px",
              }}
              variant="outlined"
              fullWidth
              disabled
              multiline
              rows={width < 769 ? 7 : 10}
              value={`<iframe width="100%" height="750" src="${encryptedUrl}" title="ADS Leaderboard" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen scrolling="no"></iframe>`}
              inputProps={{
                sx: {
                  fontFamily: "monospace",
                },
              }}
            />
          </Box>
          <Box
            sx={{
              marginBottom: "20px",
              display: "flex",
              justifyContent: "center",
            }}
            onClick={handleClickCloseDialog}
          >
            <Button variant="contained" size="medium">
              {t("myExpariences.stats.leaderboards.closeDialog")}
            </Button>
          </Box>
        </Dialog>
        <Box
          className={classes.leaderBoardDialogContainer}
          sx={{ margin: standalone ? "0 auto" : "1rem auto" }}
        >
          {!standalone && (
            <>
              <IconButton
                className={classes.leaderBoardShareButton}
                onClick={handleClickOpenDialog}
              >
                <ShareIcon />
              </IconButton>
              <IconButton
                size="large"
                className={classes.leaderboardAddButton}
                onClick={() => setAddScoreModal(true)}
              >
                <AddIcon />
              </IconButton>
              <IconButton
                size="large"
                className={classes.leaderboardDownloadButton}
                onClick={() => exportData()}
              >
                <FileDownload />
              </IconButton>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  maxWidth: "400px",
                }}
              >
                <FormControl fullWidth>
                  <InputLabel id="GameCategory-select">
                    {t("myExpariences.stats.leaderboards.gameCategory")}
                  </InputLabel>
                  <Select
                    labelId="GameCategory-select"
                    id="GameCategory-select"
                    value={state.gameCategory}
                    label={t("myExpariences.stats.leaderboards.gameCategory")}
                    onChange={(e) => {
                      setFetching(true);
                      setState({ ...state, gameCategory: e.target.value });
                      const inter = setTimeout(() => {
                        setFetching(false);
                        clearTimeout(inter);
                      }, 500);
                    }}
                  >
                    {[
                      ...new Set(
                        gamesList.map((item) => {
                          return item.type.replace(/[0-9]/g, "");
                        })
                      ),
                    ].map((game, index) => (
                      <MenuItem value={game} key={`game-type-${index + 1}`}>
                        {game}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              {iconButtonList[state.gameCategory]}
            </>
          )}
        </Box>
        {fetching ? (
          <div className="loading-container">
            <CircularProgress size={50} />
            <Typography>{t("loaders.fetching")}</Typography>
          </div>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignitems: "space-between",
            }}
          >
            {standalone && (
              <img
                src={xpInfos?.logoUrl}
                alt="exparience logo"
                style={{
                  height: "150px",
                  margin: "1rem 0",
                  objectFit: "contain",
                }}
              />
            )}
            <Box
              className={
                standalone ? classes.standaloneLeaderBoard : classes.leaderBoard
              }
            >
              {((xpInfos?.leaderboardLogo && width < 769) ||
                (gamesList &&
                  gamesList[gameIndex]?.leaderboardLogo &&
                  width < 769)) && (
                <Box className={classes.leaderBoardHeaderLogoMobileContainer}>
                  <img
                    className={classes.leaderBoardHeaderLogoMobile}
                    src={
                      xpInfos?.leaderboardLogo ||
                      gamesList[gameIndex].leaderboardLogo
                    }
                    alt={t("myExpariences.stats.leaderboards.logoAlt")}
                  />
                </Box>
              )}
              <Box className={classes.leaderBoardHeader}>
                {(xpInfos?.leaderboardLogo ||
                  (gamesList && gamesList[gameIndex]?.leaderboardLogo)) &&
                width > 768 ? (
                  <img
                    className={classes.leaderBoardHeaderLogo}
                    src={
                      xpInfos?.leaderboardLogo ||
                      gamesList[gameIndex].leaderboardLogo
                    }
                    alt={t("myExpariences.stats.leaderboards.logoAlt")}
                  />
                ) : (
                  <Box className={classes.leaderBoardHeaderLogoPosition}>
                    <IconButton
                      sx={{
                        color: "#FFCF00",
                      }}
                      onClick={refreshData}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Box>
                )}
                <Box
                  className={classes.leaderBoardTitleContainer}
                  sx={{
                    minWidth:
                      width > 768 ? "500px" : width < 768 ? "250px" : "inherit",
                  }}
                >
                  <img
                    className={classes.leaderBoardTitleImage}
                    src="/leaderboard/coupe.png"
                    alt={t("myExpariences.stats.leaderboards.imageAlt")}
                  />
                  <Typography
                    className={classes.leaderBoardTitle}
                    component="h2"
                  >
                    LEADERBOARD - TOP 10
                  </Typography>
                  <img
                    className={classes.leaderBoardTitleImage}
                    src="/leaderboard/coupe.png"
                    alt={t("myExpariences.stats.leaderboards.imageAlt")}
                  />
                </Box>
                {(xpInfos?.leaderboardLogo ||
                  (gamesList && gamesList[gameIndex]?.leaderboardLogo)) &&
                width > 768 ? (
                  <img
                    className={classes.leaderBoardHeaderLogo}
                    src={
                      xpInfos?.leaderboardLogo ||
                      gamesList[gameIndex].leaderboardLogo
                    }
                    alt={t("myExpariences.stats.leaderboards.logoAlt")}
                  />
                ) : (
                  <Box className={classes.leaderBoardHeaderLogoPosition}>
                    {xpInfos?.arLink && (
                      <IconButton
                        sx={{
                          color: "#000000",
                          backgroundColor: "#D3AF5F",
                        }}
                        onClick={() => window.location.assign(xpInfos.arLink)}
                      >
                        <PlayArrow />
                      </IconButton>
                    )}
                  </Box>
                )}
              </Box>
              <Box className={classes.leaderBoardExparienceNameContainer}>
                <Typography
                  className={classes.leaderBoardExparienceName}
                  component="h2"
                >
                  {standalone
                    ? xpInfos.displayName || xpInfos.name
                    : exparienceDisplayName || exparienceName}
                </Typography>
              </Box>
              {standalone && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    marginBottom: "0.25rem",
                  }}
                >
                  {
                    iconsAndTitleToDisplay[decryptedValues.category][
                      decryptedValues.kind
                    ].render
                  }
                  {
                    iconsAndTitleToDisplay[decryptedValues.category][
                      decryptedValues.kind
                    ].title
                  }
                </Box>
              )}
              <Box className={classes.leaderBoardScoreContainer}>
                {data.map((score, index) => {
                  const ableToAdd =
                    isAdmin &&
                    !location.pathname.includes("leaderboard") &&
                    ((!score.hasOwnProperty("pseudo") && index === 0) ||
                      (!score.hasOwnProperty("pseudo") &&
                        data[index - 1].hasOwnProperty("pseudo")));
                  return (
                    <Box
                      key={`score-n-${index + 1}`}
                      className={classes.leaderBoardScore}
                    >
                      <Box className={classes.leaderBoardScoreEntry}>
                        <Typography
                          className={classes.leaderBoardScoreRank}
                          component="p"
                        >
                          {index + 1}
                        </Typography>

                        {ableToAdd ? (
                          <Box
                            sx={{
                              width: "100%",
                              display: "flex",
                              justifyContent: "center",
                              position: "relative",
                            }}
                          >
                            <Box
                              sx={{
                                marginRight: width < 769 ? "1rem" : "4rem",
                              }}
                            />
                            <IconButton
                              size="large"
                              sx={{
                                color: "#FFCF00",
                                marginRight: width < 769 ? "3rem" : "6rem",
                              }}
                              onClick={() => setAddScoreModal(true)}
                            >
                              <AddIcon />
                            </IconButton>
                          </Box>
                        ) : (
                          <>
                            <Box
                              className={classes.leaderBoardScoreFlagContainer}
                            >
                              {score.flag && (
                                <img
                                  className={classes.leaderBoardScoreFlagImage}
                                  alt={t(
                                    "myExpariences.stats.leaderboards.flagAlt"
                                  )}
                                  src={score.flag}
                                />
                              )}
                            </Box>
                            <Typography
                              className={classes.leaderBoardScorePseudo}
                              component="span"
                            >
                              {score.pseudo}
                            </Typography>
                            <Typography
                              className={classes.leaderBoardScoreScore}
                            >
                              {score.score
                                ? score.score.time
                                  ? formatTimeScore(score.score.time)
                                  : score.score.score
                                  ? score.score.score
                                  : 0
                                : ""}
                            </Typography>
                            {isAdmin &&
                              !location.pathname.includes("leaderboard") &&
                              score.score && (
                                <IconButton
                                  size="large"
                                  sx={{ color: "#FFCF00" }}
                                  onClick={() => deleteScore(score._id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )}
                          </>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
              <Dialog
                open={addScoreModal}
                onClose={() => setAddScoreModal(false)}
              >
                <DialogTitle sx={{ marginBottom: "20px" }}>
                  {t("myExpariences.stats.leaderboards.addScore")}
                </DialogTitle>
                <DialogContent sx={{ minWidth: "300px" }}>
                  <Autocomplete
                    label={t(
                      "myExpariences.stats.leaderboards.playerNameSearch"
                    )}
                    id="autocomplete-guests"
                    freeSolo={true}
                    sx={{ margin: "1rem 0 " }}
                    options={
                      fetchedUsers ? fetchedUsers.map((user) => user) : []
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    loading={loading}
                    getOptionLabel={(option) => {
                      return `${option.login} - ${option.email}`;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t(
                          "myExpariences.stats.leaderboards.playerNameSearch"
                        )}
                        variant="outlined"
                        onChange={(e) => setSearchUser(e.target.value)}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <React.Fragment>
                              {loading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </React.Fragment>
                          ),
                        }}
                      />
                    )}
                    value={newScore.user}
                    onChange={(e, value) => {
                      if (value) {
                        const tmp = { ...newScore };
                        tmp.user = {
                          login: value.login,
                          id: value.id,
                          email: value.email,
                        };
                        if (value.pseudo) tmp.pseudo = value.pseudo;
                        setNewScore(tmp);
                      }
                    }}
                  />
                  <TextField
                    sx={{ marginBottom: "1rem" }}
                    fullWidth
                    id="playerName"
                    label="playerName"
                    name="Pseudo"
                    value={newScore.pseudo}
                    onChange={(e) => {
                      const tmp = { ...newScore };
                      tmp.pseudo = e.target.value;
                      setNewScore(tmp);
                    }}
                  />
                  <Autocomplete
                    id="flags-autocomplete"
                    value={newScore.flag}
                    options={countries.map((c) => ({
                      name: c.name,
                      emoji: getCountryCodeFromFlagPath(c.flags.png),
                      flag: c.flags.png,
                    }))}
                    getOptionLabel={(option) => option.name || ""}
                    getOptionDisabled={(option) => !option.emoji}
                    isOptionEqualToValue={(option, value) => {
                      return option.emoji === value.emoji;
                    }}
                    PaperComponent={(props) => {
                      return (
                        <Paper
                          elavation={12}
                          {...props}
                          sx={{
                            border: "1px rgba(255, 255, 255, 0.6) solid",
                          }}
                        />
                      );
                    }}
                    renderOption={(props, option) => (
                      <Box
                        component="li"
                        sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                        {...props}
                      >
                        <img
                          loading="lazy"
                          width="20"
                          src={option.flag}
                          alt=""
                        />
                        {option.name}
                      </Box>
                    )}
                    onChange={(e, value) => {
                      if (value) {
                        const tmp = { ...newScore };
                        tmp.flag = value;
                        setNewScore(tmp);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("myExpariences.countrySelection")}
                        variant="outlined"
                        inputProps={{
                          ...params.inputProps,
                        }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: newScore.flag?.flag ? (
                            <InputAdornment position="start">
                              <img
                                src={newScore.flag?.flag}
                                style={{ width: "30px" }}
                                alt="selected flag"
                              />
                            </InputAdornment>
                          ) : null,
                        }}
                      />
                    )}
                  />
                  <TextField
                    sx={{ margin: "1rem 0" }}
                    fullWidth
                    id="score"
                    label="Score"
                    name="Score"
                    placeholder={
                      // state.gameKind === "route"
                      // ?
                      // t(
                      //     "myExpariences.stats.leaderboards.scoreTimePlaceholder"
                      //   )
                      // :
                      t(
                        "myExpariences.stats.leaderboards.scorePointsPlaceholder"
                      )
                    }
                    value={newScore.score}
                    helperText={
                      // state.gameKind === "route"
                      //   ?
                      // t(
                      //     "myExpariences.stats.leaderboards.scoreTimePlaceholder"
                      //   )
                      // :
                      t(
                        "myExpariences.stats.leaderboards.scorePointsPlaceholder"
                      )
                    }
                    onChange={(e) => {
                      const tmp = { ...newScore };
                      tmp.score = e.target.value;
                      setNewScore(tmp);
                    }}
                  />
                </DialogContent>
                <DialogActions>
                  <Button
                    color="primary"
                    onClick={addScoreToLeaderboard}
                    disabled={savingData || checkForm()}
                  >
                    {savingData ? (
                      <CircularProgress size={28} />
                    ) : (
                      t("myExpariences.stats.leaderboards.acceptButton")
                    )}
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          </Box>
        )}
      </Root>
    );
  }
};

export default GameLeaderboards;
